import { CheckCircle2, Minus, RefreshCw, Sparkles, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { monthDay } from '../../shared/dates';
import { formatWeight, type WeightUnit } from '../../shared/units';
import { useRefreshInsight, useRefreshRecap, useWeeklyRecap, useWeightInsight } from '../api/hooks';
import { AI_OFF_FIX, useAIOffReason } from '../lib/aiOff';
import { useUI } from '../lib/ui';

function AILabel({ text }: { text: string }) {
  return (
    <span className="eyebrow eyebrow--ai">
      <Sparkles size={13} /> {text}
    </span>
  );
}

function AIError({ message }: { message: string }) {
  return <p className="small muted">{message}</p>;
}

export function InsightCard({ aiEnabled }: { aiEnabled: boolean }) {
  const { today, toast } = useUI();
  const insight = useWeightInsight(today, aiEnabled);
  const refresh = useRefreshInsight();
  const offReason = useAIOffReason();
  const data = insight.data?.insight;

  if (!aiEnabled) {
    return (
      <div className="card card--pad card--ai">
        <AILabel text="Weight insight" />
        <p className="small muted" style={{ marginTop: 8 }}>
          {offReason === 'setup'
            ? "Turn on AI (GEMINI_API_KEY) to get a plain-language read on your trend: what's water weight, what's real, and what to try next."
            : `Weight insights need AI. ${AI_OFF_FIX[offReason]}`}
        </p>
      </div>
    );
  }

  return (
    <section className="card card--pad card--ai" aria-busy={insight.isFetching || refresh.isPending}>
      <div className="card__head" style={{ marginBottom: 8 }}>
        <AILabel text="What's driving your trend" />
        <button
          className="btn btn--quiet btn--icon btn--xs"
          aria-label="Refresh insight"
          disabled={refresh.isPending || insight.isFetching}
          onClick={() => refresh.mutate(today, { onError: (e) => toast(e.message, { tone: 'error' }) })}
        >
          <RefreshCw size={14} />
        </button>
      </div>
      {(insight.isLoading || refresh.isPending) && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="skeleton" style={{ height: 24, width: '70%' }} />
          <div className="skeleton" style={{ height: 52 }} />
        </div>
      )}
      {insight.isError && !refresh.isPending && <AIError message={insight.error.message} />}
      {!insight.isLoading && !refresh.isPending && !insight.isError && !data && (
        <p className="small muted">Log a few more weigh-ins and we'll explain your trend here.</p>
      )}
      {data && !refresh.isPending && (
        <>
          <h3 style={{ fontSize: 20 }}>{data.headline}</h3>
          <p style={{ marginTop: 8, color: 'var(--ink-2)' }}>{data.body}</p>
          {data.drivers.length > 0 && (
            <div className="drivers">
              {data.drivers.map((d) => {
                const Icon = d.tone === 'up' ? TrendingUp : d.tone === 'down' ? TrendingDown : Minus;
                return (
                  <div className="driver" key={d.label}>
                    <span className={`driver__icon driver__icon--${d.tone}`} aria-hidden="true">
                      <Icon size={15} />
                    </span>
                    <span>
                      <b>{d.label}.</b> <span className="muted">{d.detail}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {data.suggestion && (
            <div className="banner banner--ai" style={{ marginTop: 14 }}>
              <Target size={17} />
              <span className="small">{data.suggestion}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function RecapCard({ aiEnabled, unit }: { aiEnabled: boolean; unit: WeightUnit }) {
  const { today, toast } = useUI();
  const recap = useWeeklyRecap(today, aiEnabled);
  const refresh = useRefreshRecap();
  const data = recap.data?.recap;
  if (!aiEnabled) return null;

  return (
    <section className="card card--pad" aria-busy={recap.isFetching || refresh.isPending}>
      <div className="card__head" style={{ marginBottom: 8 }}>
        <AILabel text={data ? `Week of ${monthDay(data.weekStart)}` : 'Weekly check-in'} />
        <button
          className="btn btn--quiet btn--icon btn--xs"
          aria-label="Refresh weekly check-in"
          disabled={refresh.isPending || recap.isFetching}
          onClick={() => refresh.mutate(today, { onError: (e) => toast(e.message, { tone: 'error' }) })}
        >
          <RefreshCw size={14} />
        </button>
      </div>
      {(recap.isLoading || refresh.isPending) && <div className="skeleton" style={{ height: 96 }} />}
      {recap.isError && !refresh.isPending && <AIError message={recap.error.message} />}
      {!recap.isLoading && !recap.isError && !data && !refresh.isPending && <p className="small muted">Your first weekly check-in appears after a week of logging.</p>}
      {data && !refresh.isPending && (
        <div className="stack" style={{ gap: 12 }}>
          <h3 style={{ fontSize: 20 }}>{data.title}</h3>
          <p style={{ color: 'var(--ink-2)' }}>{data.summary}</p>
          <div className="grid-3">
            <div className="card--soft card" style={{ padding: 12 }}>
              <div className="stat__label">Days logged</div>
              <div className="stat__value num" style={{ fontSize: 22 }}>
                {data.stats.daysLogged}/7
              </div>
            </div>
            <div className="card--soft card" style={{ padding: 12 }}>
              <div className="stat__label">Avg kcal</div>
              <div className="stat__value num" style={{ fontSize: 22 }}>
                {data.stats.avgKcal?.toLocaleString('en-US') ?? '—'}
              </div>
            </div>
            <div className="card--soft card" style={{ padding: 12 }}>
              <div className="stat__label">Trend</div>
              <div className="stat__value num" style={{ fontSize: 22 }}>
                {data.stats.weightChangeKg === null ? '—' : formatWeight(data.stats.weightChangeKg, unit, { signed: true })}
              </div>
            </div>
          </div>
          {data.wins.length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              {data.wins.map((w) => (
                <div key={w} className="row small" style={{ alignItems: 'flex-start' }}>
                  <CheckCircle2 size={16} style={{ color: 'var(--good)', flex: 'none', marginTop: 2 }} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
          <div className="banner banner--ai">
            <Target size={17} />
            <span className="small">
              <b>Next week:</b> {data.focus}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
